import { LowerCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../core/services/auth/auth.service';
import { AdminReleasesService } from '../../core/services/release/admin-releases.service';
import { Release, ReleaseStatus } from '../../models/release';
import { SelectComponent, ToastService } from '../../ui';
import { formatRange, toDateInput, toIso } from './release-dates';

/** Local edit-form state for an expanded release row. Plain (ngModel-bound) object. */
interface EditState {
  status: ReleaseStatus;
  openOn: string; // yyyy-MM-dd
  signOffDue: string; // yyyy-MM-dd
}

/** Inline new-release row state. */
interface CreateState {
  name: string;
  azureId: number | null;
  openOn: string;
  signOffDue: string;
  saving: boolean;
  importing: boolean;
}

/**
 * Admin → Manage releases (PBI 55094). Collapsible rows; each expands to edit
 * status + open / sign-off-due dates, and links to the per-release configure page
 * for plans and cases. Shipped/Archived releases are locked (read-only) except for
 * the status select, which is the only control that can re-enable them. Create is
 * inline and Azure-backed: saving imports every suite/plan/case from Azure and
 * lands on the configure page.
 */
@Component({
  selector: 'app-admin-releases',
  standalone: true,
  imports: [FormsModule, RouterLink, LowerCasePipe, SelectComponent],
  templateUrl: './admin-releases.component.html',
  styleUrl: './admin-releases.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminReleasesComponent implements OnInit {
  private adminReleases = inject(AdminReleasesService);
  private toast = inject(ToastService);
  private auth = inject(AuthService);
  private router = inject(Router);

  readonly statuses: ReleaseStatus[] = ['Active', 'Shipped', 'Held', 'Archived'];
  readonly statusOptions = this.statuses.map((s) => ({ value: s, label: s }));
  /** Placeholder rows rendered while releases load. */
  readonly skeletonRows = [0, 1, 2, 3];

  readonly releases = signal<Release[]>([]);
  readonly isLoading = signal(false);
  readonly expandedIds = signal<Set<number>>(new Set());
  readonly savingIds = signal<Set<number>>(new Set());

  /** ngModel-bound edit state keyed by release id (plain object, not a signal). */
  edits: Record<number, EditState> = {};
  /** ngModel-bound inline-create row (null when not creating). */
  creating: CreateState | null = null;

  private get userId(): number {
    return this.auth.userSig()?.userId ?? 1;
  }

  ngOnInit(): void {
    this.load();
  }

  // ---- row state ----

  isExpanded(r: Release): boolean {
    return this.expandedIds().has(r.releaseId);
  }

  /** Lock is driven by the persisted status: Shipped and Archived are read-only. */
  isLocked(r: Release): boolean {
    return r.status === 'Shipped' || r.status === 'Archived';
  }

  isSaving(r: Release): boolean {
    return this.savingIds().has(r.releaseId);
  }

  statusClass(status: ReleaseStatus): string {
    return status.toLowerCase();
  }

  dateRange(r: Release): string {
    const start = r.openOnUtc ?? r.createdOnUtc;
    const end = r.signOffDueOnUtc ?? r.closedOnUtc;
    return formatRange(start, end);
  }

  // ---- expand / edit ----

  toggle(r: Release): void {
    const set = new Set(this.expandedIds());
    if (set.has(r.releaseId)) {
      set.delete(r.releaseId);
    } else {
      set.add(r.releaseId);
      this.edits[r.releaseId] = editFrom(r);
    }
    this.expandedIds.set(set);
  }

  onStatusChange(r: Release): void {
    // A locked release exposes only the status select; changing it persists
    // immediately so the row can be unlocked (or moved between locked states).
    if (this.isLocked(r)) this.save(r);
  }

  save(r: Release): void {
    const edit = this.edits[r.releaseId];
    if (!edit) return;

    const set = new Set(this.savingIds());
    set.add(r.releaseId);
    this.savingIds.set(set);

    this.adminReleases
      .update(
        r.releaseId,
        {
          status: edit.status,
          openOnUtc: toIso(edit.openOn),
          signOffDueOnUtc: toIso(edit.signOffDue),
        },
        this.userId,
      )
      .pipe(finalize(() => this.clearSaving(r.releaseId)))
      .subscribe({
        next: (updated) => {
          this.replaceRelease(updated);
          this.edits[updated.releaseId] = editFrom(updated);
          this.toast.success('Release updated');
        },
        error: () => this.toast.error('Failed to update the release.'),
      });
  }

  // ---- inline create ----

  startCreate(): void {
    this.creating = {
      name: '',
      azureId: null,
      openOn: '',
      signOffDue: '',
      saving: false,
      importing: false,
    };
  }

  cancelCreate(): void {
    this.creating = null;
  }

  saveCreate(): void {
    const c = this.creating;
    if (!c || !c.name.trim() || c.azureId == null) return;
    c.saving = true;

    this.adminReleases.create(c.azureId, c.name.trim(), this.userId).subscribe({
      next: (created) => {
        const proceed = () => this.importAndOpen(created.releaseId);
        // The Azure-backed insert doesn't take dates; persist them in a follow-up
        // update when the admin supplied them.
        if (c.openOn || c.signOffDue) {
          this.adminReleases
            .update(
              created.releaseId,
              {
                status: 'Active',
                openOnUtc: toIso(c.openOn),
                signOffDueOnUtc: toIso(c.signOffDue),
              },
              this.userId,
            )
            .subscribe({ next: proceed, error: proceed });
        } else {
          proceed();
        }
      },
      error: () => {
        this.toast.error('Failed to create the release. Check the Azure release id.');
        c.saving = false;
      },
    });
  }

  /**
   * A new release starts fully populated: pull every suite/plan/case from Azure
   * and land on the configure page, where the admin turns off what doesn't apply.
   */
  private importAndOpen(releaseId: number): void {
    if (this.creating) this.creating.importing = true;

    this.adminReleases.importFromAzure(releaseId, this.userId).subscribe({
      next: () => {
        this.toast.success('Release created and plans imported');
        this.creating = null;
        void this.router.navigate(['/admin/releases', releaseId]);
      },
      error: () => {
        // The release itself was created; the configure page has an Import
        // button so the admin can retry the Azure pull from there.
        this.toast.error('Release created, but the Azure import failed. Retry from Configure.');
        this.creating = null;
        void this.router.navigate(['/admin/releases', releaseId]);
      },
    });
  }

  // ---- data ----

  private load(): void {
    this.isLoading.set(true);
    this.adminReleases
      .getAll()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (releases) => this.releases.set(releases),
        error: () => this.toast.error('Failed to load releases.'),
      });
  }

  private refreshRelease(releaseId: number): void {
    this.adminReleases.getById(releaseId).subscribe({
      next: (full) => this.replaceRelease(full),
    });
  }

  private replaceRelease(updated: Release): void {
    this.releases.update((list) =>
      list.map((r) => (r.releaseId === updated.releaseId ? { ...r, ...updated } : r)),
    );
  }

  private clearSaving(releaseId: number): void {
    const set = new Set(this.savingIds());
    set.delete(releaseId);
    this.savingIds.set(set);
  }
}

/** Build edit-form state from a release. */
function editFrom(r: Release): EditState {
  return {
    status: r.status,
    openOn: toDateInput(r.openOnUtc),
    signOffDue: toDateInput(r.signOffDueOnUtc),
  };
}
